import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AsyncPaginate, wrapMenuList } from "react-select-async-paginate";
import { components } from "react-select";
import { toast } from "react-toastify";
import { ceil } from "lodash";
import companyApi from "api/companyApi";
import NO_IMAGE from "../../assets/images/no-image-icon.png";
import i18n from "i18n";

interface Option {
  label: string | any;
  value: string;
}
interface DropdownOptionProps {
  name?: string;
  listCheckedUsers?: string | null;
  debounceTimeout?: number;
  onChangeSelect?: (params: Option) => void;
  isMulti?: boolean;
  placeholder?: string;
  isShowIconCheck?: boolean;
  hideSelectedOptions?: boolean;
  isAllOption?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  keySetValue?: string;
}
const LIMIT = 30;

const DropdownAsyncCompanies = ({
  name = "",
  listCheckedUsers,
  debounceTimeout = 700,
  onChangeSelect,
  isMulti = false,
  placeholder,
  isShowIconCheck = false,
  hideSelectedOptions = true,
  isAllOption = false,
  isDisabled = false,
  isClearable = true,
  keySetValue = "",
}: DropdownOptionProps) => {
  const { t } = useTranslation();
  const [isAllCheckedUsers, setIsAllCheckedUsers] = useState(false);
  const [options, setOptions] = useState([]);
  const OPTION_ALL_DEFAULT = { label: t("All Filter"), value: "all" };

  const setOnChangeOption = (item: any) => {
    onChangeSelect && onChangeSelect(item);
  };

  const loadListAgency = async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search?: string;
  }) => {
    try {
      let params: any = { page, limit, keyword: search };

      const {
        data: { items, pagination },
        statusCode,
      } = await companyApi.companies(params);

      if (statusCode !== 200) {
        return { success: false };
      }

      const dataUsersOptions = items?.map((a: any) => {
        return {
          value: String(keySetValue ? a?.[keySetValue] : a?.id),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  height: "22px",
                  width: "22px",
                  overflow: "hidden",
                  borderRadius: "15px",
                  objectFit: "cover",
                  backgroundColor: "#eaeaea",
                  zIndex: 99,
                }}
              >
                <img
                  src={a?.logo_url || NO_IMAGE}
                  alt={a?.name}
                  height={22}
                  width={22}
                  style={{
                    minWidth: "22px",
                    overflow: "hidden",
                    borderRadius: "50%",
                    objectFit: "cover",
                    backgroundColor: "#eaeaea",
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = NO_IMAGE;
                  }}
                />
              </div>
              {a?.name}
            </div>
          ),
          data: a,
        };
      });

      return {
        code: statusCode,
        dataUsersOptions: dataUsersOptions || [],
        lastPage: ceil(pagination?.total / pagination?.page_size),
      };
    } catch (err) {
      return {
        code: 404,
        dataUsersOptions: [],
        lastPage: 1,
      };
    }
  };

  async function loadOptions(
    search: string,
    loadedOptions: any[],
    { page }: { page: number }
  ) {
    const { code, dataUsersOptions, lastPage } = await loadListAgency({
      page,
      limit: LIMIT,
      search,
    });
    let dataUsersOptionsFormat = dataUsersOptions || [];

    if (code !== 200) {
      toast(`${t("Failed to load data")}`, CONFIG_OPTION_TOAST_ERROR);
      return {
        options: [],
        hasMore: false,
      };
    }
    let lastPageFormat = lastPage ? lastPage : 1;
    let options: any = dataUsersOptionsFormat;

    if (isAllOption) {
      options = [
        ...(page === 1 ? [OPTION_ALL_DEFAULT] : []),
        ...dataUsersOptionsFormat,
      ];
    }

    setOptions(options);

    return {
      options: options,
      hasMore: lastPageFormat > page,
      additional: {
        page: page + 1,
      },
    };
  }

  const MenuList = (props: any) => {
    let childComponent = props?.children;
    if (props?.children?.length > 0) {
      const allOption = props?.children?.filter(
        (item: any) => item?.props?.value === "all"
      );
      let fixedIndex = -1;
      if (allOption) {
        fixedIndex = props?.children.indexOf(allOption?.[0]);
      }
      const fixedElement = props?.children?.[fixedIndex];

      const childComponentFormat = (
        (fixedIndex !== -1 ? props?.children.slice(1) : props?.children) || []
      )?.sort((a: any, b: any) => {
        let pointA = a.props.isSelected === true ? 1 : 0;
        let pointB = b.props.isSelected === true ? 1 : 0;
        return pointB - pointA;
      });

      if (fixedElement) {
        childComponent = [fixedElement, ...childComponentFormat];
      } else {
        childComponent = childComponentFormat;
      }
    }

    return (
      <components.MenuList {...props}>{childComponent}</components.MenuList>
    );
  };

  const OptionFormat = (props: any) => {
    if (props?.isSelected && props?.value === "all") {
      setIsAllCheckedUsers(true);
    } else if (!props?.isSelected && props?.value === "all") {
      setIsAllCheckedUsers(false);
    }

    return (
      <components.Option {...props} style={{ zIndex: 999 }}>
        <div className="d-flex align-items-center gap-2">
          {(props?.isSelected || isAllCheckedUsers) && isShowIconCheck ? (
            <i className="ri-check-line align-middle fs-14" />
          ) : (
            ""
          )}
          {props.data?.label}
        </div>
      </components.Option>
    );
  };

  const MenuListAsync = wrapMenuList(MenuList);

  useEffect(() => {
    if (
      ((listCheckedUsers as any)?.value === OPTION_ALL_DEFAULT.value ||
        !(listCheckedUsers as any)?.value) &&
      isAllOption
    ) {
      setOnChangeOption(OPTION_ALL_DEFAULT);
    }
  }, [i18n.language, isAllOption, JSON.stringify(listCheckedUsers)]);

  return (
    <AsyncPaginate
      name={name}
      isMulti={isMulti}
      isClearable={isClearable}
      // defaultOptions
      options={options}
      debounceTimeout={debounceTimeout}
      value={listCheckedUsers}
      loadOptions={loadOptions}
      placeholder={placeholder}
      className="dropdown-status-rounded"
      hideSelectedOptions={false}
      onChange={(e: any) => setOnChangeOption(e)}
      additional={{
        page: 1,
      }}
      components={{
        Option: OptionFormat,
        ...(isMulti ? { MenuList: MenuListAsync } : null),
        // MenuList: MenuListAsync,
      }}
      closeMenuOnSelect={hideSelectedOptions}
      menuPortalTarget={document.body}
      styles={{
        option: (styles: any) => {
          let styleNew = isMulti
            ? {
                ...styles,
                backgroundColor: "white",
                zIndex: 99,
                color: "black",
              }
            : styles;

          return styleNew;
        },
        control: (styles: any, { isFocused }: any) => {
          return {
            ...styles,
            borderColor: "#e9ebec",
            borderRadius: "0.6rem",
            color: "white",
            "&:hover": { borderColor: "#009ef7" },
          };
        },
        multiValue: (styles: any) => {
          return {
            ...styles,
            backgroundColor: "#009ef7",
          };
        },
        multiValueLabel: (styles: any) => ({
          ...styles,
          color: "white !important",
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 1051,
        }),
      }}
      isDisabled={isDisabled}
    />
  );
};

export default DropdownAsyncCompanies;
